import { AsyncPipe, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { desktopDto } from '@vectronic/desktop-api';
import { webDtos } from '@vectronic/openApi-admin';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { webEnums } from '@vectronic/openApi-customer';
import { Item, ItemHelp, Translator } from '@vectronic/util';
import { map, Observable, shareReplay, timer } from 'rxjs';
import { UtilService } from '../../services/util.service';
import { DetailsTopBarItemComponent } from '../details-top-bar-item/details-top-bar-item.component';

@Component({
  selector: 'vectronic-details-top-bar',
  templateUrl: './details-top-bar.component.html',
  styleUrls: ['./details-top-bar.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    DetailsTopBarItemComponent,
    MatTooltipModule,
    DatePipe,
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatMenuModule,
  ],
})
export class DetailsTopBarComponent implements OnInit, AfterViewInit, OnDestroy {
  Item = Item;
  collar = input<webDtos.CollarAdmin>();
  trap = input<webDtos.LongRangeDeviceAdmin>();
  connectedDevice = input<desktopDto.DeviceConnection>();
  isReloading = input(false);
  isSmall = input(false);
  showClose = input(false);
  hideMenu = input(false);
  dataType = input<string>();
  isFirmwareUpdateAvailable = input(false);

  navigateToPositionsPage = output<number[]>();
  navigateToMapPage = output<number[]>();
  colorChange = output<string>();
  closeSidebar = output();
  reloadConnectedDevice = output<desktopDto.DeviceConnection>();

  firmwareUpdate = output();

  isDesktop = Boolean(window.desktop || window.adminDesktop);

  @ViewChild('scrollContainer', { read: ElementRef }) scrollContainer: ElementRef;

  showArrows = signal(false);
  isLeftArrowDisabled = signal(false);
  isRightArrowDisabled = signal(false);
  private resizeObserver: ResizeObserver;

  isConnectedDeviceBtnDisabled: boolean;

  private router = inject(Router);
  private utilService = inject(UtilService);

  private customDate: Date;
  private _time$: Observable<Date>;

  get time() {
    return this._time$;
  }

  ngOnInit() {
    const connectedDevice = this.connectedDevice();
    if (!connectedDevice?.deviceTime) return;
    this.customDate = new Date(
      new Date(connectedDevice.deviceTime).getTime() +
        (Date.now() - new Date(connectedDevice.time).getTime()),
    );
    this._time$ = timer(0, 1000).pipe(
      map(() => {
        this.customDate = new Date(this.customDate.getTime() + 1000);
        return this.customDate;
      }),
      shareReplay(1),
    );
  }

  resetTime() {
    this.customDate = new Date(this.connectedDevice().deviceTime);
  }

  concatString(value: string) {
    return value?.length > 25 ? value.slice(0, 25) + '...' : value;
  }

  roundPosition(lastPosition: webDtos.GPSPosition) {
    return Translator.roundPosition(lastPosition);
  }

  onReloadDevice() {
    this.reloadConnectedDevice.emit(this.connectedDevice());
  }

  ngAfterViewInit() {
    if (this.scrollContainer) {
      this.checkScroll();
      this.resizeObserver = new ResizeObserver(() => {
        this.checkScroll();
      });
      this.resizeObserver.observe(this.scrollContainer.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.scrollContainer) {
      this.resizeObserver?.unobserve(this.scrollContainer.nativeElement);
      this.scrollContainer?.nativeElement.removeEventListener('scroll', () => this.checkScroll());
    }
  }

  scrollLeft() {
    this.scrollContainer.nativeElement.scrollBy({ left: -100, behavior: 'smooth' });
    setTimeout(() => this.checkScroll(), 300);
  }

  scrollRight() {
    this.scrollContainer.nativeElement.scrollBy({ left: 100, behavior: 'smooth' });
    setTimeout(() => this.checkScroll(), 300);
  }

  checkScroll() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer.nativeElement;
    this.isRightArrowDisabled.set(scrollWidth - clientWidth - scrollLeft <= 0);
    this.isLeftArrowDisabled.set(scrollLeft === 0);
    this.showArrows.set(scrollLeft < scrollWidth - clientWidth || scrollLeft > 0);
  }

  onClick(type: Item, id: number) {
    const route = ItemHelp.toRoute(type, id);
    if (this.isDesktop) return this.router.navigate([route]);
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    window.open(url, '_blank');
  }

  translateMortalityStatusShort(lastPositionMortalityStatus: webEnums.MortalityStatus) {
    return Translator.mortalityStatusShort(lastPositionMortalityStatus);
  }

  onClickClose() {
    this.closeSidebar.emit();
  }

  getRoundNumber(number: number) {
    return Translator.getRoundNumber(number);
  }

  hasNewerFirmware(connection?: desktopDto.DeviceConnection) {
    return this.utilService.hasNewerFirmware(connection);
  }
}
